import { FC, useState, useEffect } from 'react'
import { PerInfoBox } from '../../../../components/custom_components/InfoBox'
import { PerInfoBoxNumberElement } from '../../../../components/custom_components/InfoBox/InfoBoxNumberElement'
import { useTranslation } from 'react-i18next'
import { Select, Col, Divider } from 'antd'
import PerFlexRow from '../../../../components/custom_components/FlexRow'
import PerText from '../../../../components/custom_components/Text'
import { CompanyTinyWithName, EmployeeNameSearchInfo, Country, ActiveHrBusinessPartnerDto } from '../../../../types'
import PerButton from '../../../../components/custom_components/Button'
import { BusinessPartnerSelect } from './BusinessPartnerSelect'
import { createHRBPRelation } from '../../../../graphql/hrBusinessPartners/MutationFunctions'
import { showNotification } from '../../../../components/custom_components/Notification/notification'
const { Option } = Select

interface Props {
  numberOfRelations: number
  initialPossibleCompanies: CompanyTinyWithName[]
  country: Country
  businessPartnerRelations: ActiveHrBusinessPartnerDto[]
}

export const HrSupportHRBPInfoBox: FC<Props> = ({ numberOfRelations, initialPossibleCompanies, country, businessPartnerRelations }) => {
  const [t] = useTranslation()
  const [selectedCompanies, setCompanies] = useState<string[]>([])
  const [selectedHRBP, setHRBP] = useState<EmployeeNameSearchInfo | undefined>(undefined)
  const [possibleCompanies, setPossibleCompanies] = useState<CompanyTinyWithName[]>(initialPossibleCompanies)
  const [creationInProgress, setCreationInProgress] = useState<boolean>(false)

  useEffect(() => {
    if (!selectedHRBP) setPossibleCompanies(initialPossibleCompanies)
    else {
      const existingRelationsCompanyIds: string[] = businessPartnerRelations
        .filter((r) => r.employee.id === selectedHRBP.id)
        .map((r) => r.companyId)
      setPossibleCompanies(initialPossibleCompanies.filter((pc) => !existingRelationsCompanyIds.includes(pc.id)))
    }
  }, [selectedHRBP, initialPossibleCompanies, businessPartnerRelations])

  const handleCreateHRBPRelation = async () => {
    if (selectedHRBP && selectedCompanies.length) {
      setCreationInProgress(true)
      const res = await createHRBPRelation(selectedHRBP.id, selectedCompanies, country)

      if (!!res.errors && !!res.errors.length) {
        showNotification({
          type: 'error',
          message: t(`companySettingsView.hrBusinessPartnersTab.infoBox.notifications.error.title`),
          description: t(`companySettingsView.hrBusinessPartnersTab.infoBox.notifications.error.description`),
        })
      } else {
        setCompanies([])
        setHRBP(undefined)
        showNotification({
          type: 'success',
          message: t(`companySettingsView.hrBusinessPartnersTab.infoBox.notifications.success.title`),
          description: t(`companySettingsView.hrBusinessPartnersTab.infoBox.notifications.success.description`, {
            employeeFirstName: selectedHRBP.chosenFirstName || selectedHRBP.legalFirstName,
          }),
        })
      }
      setCreationInProgress(false)
    }
  }

  return (
    <PerInfoBox>
      <PerFlexRow align="middle" justify="space-between" width="fullWidth" className="fullHeight">
        <PerFlexRow direction="column" className="fullHeight" justify="space-between">
          <PerText appearance="tile-heading">{t('companySettingsView.hrBusinessPartnersTab.infoBox.counterHeading')}</PerText>
          <PerInfoBoxNumberElement number={numberOfRelations} />
        </PerFlexRow>

        <Divider type="vertical" className="fullHeight" />

        <Col span={18} className="fullHeight">
          <PerFlexRow direction="column" justify="space-between" width="fullWidth" className="fullHeight">
            <PerText appearance="tile-heading">{t('companySettingsView.hrBusinessPartnersTab.infoBox.filterHeading')}</PerText>

            <PerFlexRow direction="column">
              <PerFlexRow width="fullWidth" justify="space-between">
                <Col span={10}>
                  <PerFlexRow direction="column" width="fullWidth">
                    <PerText appearance="label-small" fontWeight="bold" transform="uppercase" mb="xxxs">
                      {t('companySettingsView.hrBusinessPartnersTab.infoBox.employee')}
                    </PerText>
                    <BusinessPartnerSelect
                      onSelectValue={setHRBP}
                      notFoundContentText={t('companySettingsView.hrBusinessPartnersTab.infoBox.notFoundSearchSelectContent')}
                      placeholderText={t('companySettingsView.hrBusinessPartnersTab.infoBox.selectEmployee')}
                      selectedValue={selectedHRBP}
                      country={country}
                    />
                  </PerFlexRow>
                </Col>
                <Col span={14}>
                  <PerFlexRow direction="column" width="fullWidth">
                    <PerText appearance="label-small" fontWeight="bold" transform="uppercase" mb="xxxs">
                      {t('companySettingsView.hrBusinessPartnersTab.infoBox.companies')}
                    </PerText>
                    <Select
                      mode="multiple"
                      onChange={(values: string[]) => {
                        setCompanies(values)
                      }}
                      value={selectedCompanies}
                      placeholder={t('companySettingsView.hrBusinessPartnersTab.infoBox.selectCompanies')}
                      style={{ width: '100%' }}
                      maxTagCount={2}
                    >
                      {[...possibleCompanies]
                        .sort((companyA: CompanyTinyWithName, companyB: CompanyTinyWithName) => companyA.name.localeCompare(companyB.name))
                        .map((company) => (
                          <Option key={company.id} value={company.id}>
                            {company.name}
                          </Option>
                        ))}
                    </Select>
                  </PerFlexRow>
                </Col>
              </PerFlexRow>
              <PerFlexRow justify="end" mt="s" align="middle">
                <PerButton
                  text={t('companySettingsView.hrBusinessPartnersTab.infoBox.add')}
                  color="success"
                  type="button"
                  variant="pastel"
                  before={creationInProgress ? 'loading_fill' : 'add_stroke'}
                  onClick={handleCreateHRBPRelation}
                  disabled={!selectedHRBP || !selectedCompanies.length || creationInProgress}
                />
              </PerFlexRow>
            </PerFlexRow>
          </PerFlexRow>
        </Col>
      </PerFlexRow>
    </PerInfoBox>
  )
}
